import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { ReactComponent as IcfLogoRaw } from "../assets/images/logos/LogoSVG.svg";

// Logo animado con contorno y relleno
const IcfLogo = (props) => {
  const logoRef = useRef(null);
  useEffect(() => {
    const svg = logoRef.current;
    if (!svg) return;
    // Selecciona todos los paths
    const paths = svg.querySelectorAll('path');
    paths.forEach((path, i) => {
      path.style.stroke = '#fff';
      path.style.strokeWidth = '0.7';
      path.style.fillOpacity = '0';
      path.style.strokeDasharray = path.getTotalLength();
      path.style.strokeDashoffset = path.getTotalLength();
      path.style.transition = 'none';
    });
    // Animación: contorno
    setTimeout(() => {
      paths.forEach((path, i) => {
        path.style.transition = 'stroke-dashoffset 2.2s cubic-bezier(.77,0,.18,1)';
        path.style.strokeDashoffset = '0';
      });
    }, 100);
    // Animación: relleno
    setTimeout(() => {
      paths.forEach((path, i) => {
        path.style.transition = 'fill-opacity 2.2s cubic-bezier(.77,0,.18,1)';
        path.style.fillOpacity = '1';
      });
    }, 2400);
    // Animación: difuminar contorno blanco
    setTimeout(() => {
      paths.forEach((path, i) => {
        path.style.transition = 'stroke-opacity 1.2s cubic-bezier(.77,0,.18,1)';
        path.style.strokeOpacity = '0';
      });
    }, 4800);
    // Contorno negro final
      // No stroke negro final
  }, []);
  return (
    <span style={{display:'inline-block', position:'relative'}}>
      <IcfLogoRaw ref={logoRef} {...props} />
      <style>{`
        svg[aria-label], svg[aria-label] path {
          transition: none;
        }
      `}</style>
    </span>
  );
};

const Header = ({ navigationItems, activeSection, scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (itemId) => {
    if (scrollToSection) {
      scrollToSection(itemId);
    }
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (itemId) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  // Cierra el dropdown si se hace clic fuera
  useEffect(() => {
    if (!activeDropdown) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  return (
    <>
      {/* Header Principal */}
 <header
  className={`fixed w-full z-50 transition-all duration-700 ease-out ${
    scrollY > 100
      ? "bg-gradient-to-r from-yellow-500 to-amber-500 shadow-2xl shadow-yellow-500/30"
      : "bg-gradient-to-r from-orange-500 to-orange-600 backdrop-blur-sm"
  }`}
>
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="flex items-center justify-between py-4">
      {/* Logo clickable para ir a inicio */}
      <div className="flex items-center flex-shrink-0 cursor-pointer" onClick={() => handleNavigation('inicio')}>
        <IcfLogo
          className="w-24 h-24 object-contain transition-transform duration-300 hover:scale-105 p-2"
          style={{
            filter:
              "drop-shadow(0 2px 8px rgba(0,0,0,0.75)) brightness(1.1) contrast(1.2)"
          }}
          aria-label="AVA Logo"
        />
      </div>

      {/* Catálogos con flex-around */}
      <nav className="hidden lg:flex flex-1 justify-center">
        <div
          className={`flex-nowrap flex items-center justify-center gap-2 rounded-full p-2 transition-all duration-500 max-w-7xl ${
            scrollY > 100
              ? "bg-yellow-200/40 shadow-inner shadow-yellow-300/50 backdrop-blur-sm"
              : "bg-orange-200/30 backdrop-blur-sm shadow-inner shadow-orange-300/30"
          }`}
        >
          {navigationItems.map((item) => (
            <div key={item.id} className="relative flex-shrink-0">
              <div className="relative flex items-center">
                <button
                  onClick={() => handleNavigation(item.id)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all duration-300 font-medium text-sm relative overflow-visible group ${
                    activeSection === item.id
                      ? scrollY > 100
                        ? "bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg shadow-yellow-500/40"
                        : "bg-gradient-to-r from-orange-700 to-orange-600 text-white shadow-lg shadow-orange-400/50"
                      : scrollY > 100
                      ? "text-amber-800 hover:bg-yellow-300/30 hover:text-amber-900"
                      : "text-white hover:bg-orange-700/30 hover:text-white"
                  }`}
                >
                  {item.icon && <item.icon className="w-6 h-6 flex-shrink-0" />}
                  <span className="font-semibold tracking-wide break-words text-center leading-tight max-w-[90px]">
                    {item.name}
                  </span>
                </button>
                {item.subItems && item.subItems.length > 0 && (
                  <button
                    type="button"
                    aria-label="Abrir submenú"
                    onClick={e => {
                      e.stopPropagation();
                      toggleDropdown(item.id);
                    }}
                    className="ml-1 absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-transparent border-none p-0 m-0"
                    tabIndex={0}
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ml-1 ${
                        activeDropdown === item.id ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                )}
                {/* Submenú desktop */}
                {item.subItems && item.subItems.length > 0 && activeDropdown === item.id && (
                  <div
                    ref={dropdownRef}
                    className="absolute left-0 top-full mt-1 min-w-[180px] bg-white rounded-xl shadow-xl border border-orange-200 z-30"
                  >
                    <div className="py-2">
                      {item.subItems.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => {
                            handleNavigation(sub.id);
                            setActiveDropdown(null);
                          }}
                          className="w-full text-left px-6 py-3 text-orange-700 hover:text-orange-900 hover:bg-orange-50 rounded-lg transition-colors font-medium flex items-center space-x-2"
                        >
                          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                          <span>{sub.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Botón móvil */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className={`lg:hidden p-3 rounded-full transition-all duration-300 ${
          scrollY > 100
            ? "text-amber-800 bg-yellow-200/60 hover:bg-yellow-300/60"
            : "text-white bg-orange-700/40 hover:bg-orange-700/60"
        }`}
      >
        <Menu />
      </button>
    </div>
  </div>
</header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
            isMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 w-80 md:w-96 max-w-full bg-white/95 backdrop-blur-xl shadow-2xl transition-transform duration-500 ease-out ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          } overflow-x-hidden`}
          style={{height: 'auto', maxHeight: '100vh', overflowY: 'auto'}}
        >
          <div className="p-6 border-b border-orange-200/50 bg-gradient-to-r from-orange-500 to-orange-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/95 rounded-full flex items-center justify-center shadow-lg">
                  <IcfLogo
                    className="w-12 h-12 object-contain"
                    style={{
                      filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.25)) brightness(1.1) contrast(1.2)"
                    }}
                    aria-label="Campo Fresco ICF Logo"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-white">ALMACENES VACA</h3>
                  <p className="text-xs text-orange-100">Menú Principal</p>
                </div>
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 text-white hover:text-orange-200 rounded-full hover:bg-orange-600/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="py-6 max-h-[calc(100vh-100px)] overflow-y-auto">
            {navigationItems.map((item) => (
              <div key={item.id} className="mb-2">
                <div className={`w-full flex items-center justify-between px-6 py-4 transition-all duration-200 ${
                  activeSection === item.id
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg mx-4 rounded-xl"
                    : "text-orange-700 hover:bg-orange-50 mx-2 rounded-lg"
                }`}>
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {item.icon && (
                      <div
                        className={`p-2 rounded-lg ${
                          activeSection === item.id
                            ? "bg-white/20"
                            : "bg-gradient-to-r from-orange-100 to-yellow-100"
                        }`}
                      >
                        <item.icon
                          className={`w-5 h-5 ${
                            activeSection === item.id
                              ? "text-white"
                              : "text-orange-600"
                          }`}
                        />
                      </div>
                    )}
                    <button
                      onClick={() => handleNavigation(item.id)}
                      className="font-semibold text-left flex-1 min-w-0 bg-transparent border-none outline-none p-0 m-0"
                      style={{background:'none'}}
                    >
                      {item.name}
                    </button>
                  </div>
                  {item.subItems && item.subItems.length > 0 && (
                    <button
                      type="button"
                      aria-label="Abrir submenú"
                      onClick={e => {
                        e.stopPropagation();
                        toggleDropdown(item.id);
                      }}
                      className="ml-2 bg-transparent border-none p-0 m-0"
                      tabIndex={0}
                    >
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-200 ${
                          activeDropdown === item.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                </div>

                {/* Submenú móvil */}
                {item.subItems && item.subItems.length > 0 && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      activeDropdown === item.id
                        ? "max-h-96 opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="pl-6 pr-4 py-2 space-y-1">
                      {item.subItems.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => handleNavigation(sub.id)}
                          className="w-full text-left px-4 py-3 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors font-medium flex items-center space-x-2"
                        >
                          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                          <span>{sub.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
